/**
 * Los trabajos en segundo plano del proyecto (R-17). Se pregunta al motor cuando cambia el proyecto
 * o pasa algo (pulso) y, mientras haya alguno en curso, cada cinco segundos. Cuando uno termina se
 * avisa (`alTerminar`), para que las pantallas relean su resultado: el informe aparece solo.
 *
 * CH-6: y se le dice al autor, que estaba haciendo otra cosa. Los terminados quedan en `terminados`
 * hasta que los cierra; con la pestaña en segundo plano, el título de la pestaña lo dice.
 */
import { useEffect, useRef, useState } from "react";
import { trabajos } from "../api";
import type { Trabajo } from "../api";

export function useTrabajos(dir: string, pulso: number, alTerminar: () => void) {
  const [lista, setLista] = useState<Trabajo[]>([]);
  const [tic, setTic] = useState(0);
  const antes = useRef<Record<string, string>>({});
  const [terminados, setTerminados] = useState<Trabajo[]>([]);

  useEffect(() => {
    if (!dir) { setLista([]); return; }
    let vigente = true;
    let reintento = 0;
    trabajos(dir).then((l) => {
      if (!vigente) return;
      // Sin respuesta, no se borra nada: se vuelve a preguntar en cinco segundos.
      if (l == null) { reintento = window.setTimeout(() => setTic((n) => n + 1), 5000); return; }
      const acaban = l.filter((t) => antes.current[t.id] === "en_curso" && t.estado !== "en_curso");
      const termino = acaban.length > 0;
      antes.current = Object.fromEntries(l.map((t) => [t.id, t.estado]));
      if (termino) {
        setTerminados((v) => [...acaban, ...v.filter((x) => !acaban.some((a) => a.id === x.id))].slice(0, 3));
        avisarFuera(acaban);
      }
      setLista(l);
      if (termino) alTerminar();
    });
    return () => { vigente = false; window.clearTimeout(reintento); };
  }, [dir, pulso, tic]);
  // Otro proyecto, otros avisos.
  useEffect(() => { setTerminados([]); }, [dir]);

  const hayVivos = lista.some((t) => t.estado === "en_curso");
  useEffect(() => {
    if (!hayVivos) return;
    const i = window.setInterval(() => setTic((n) => n + 1), 5000);
    return () => window.clearInterval(i);
  }, [hayVivos]);

  return {
    lista,
    vivos: lista.filter((t) => t.estado === "en_curso"),
    ultimo: (tipo: string) => lista.find((t) => t.tipo === tipo) ?? null,
    refrescar: () => setTic((n) => n + 1),
    terminados,
    cerrar: (id: string) => setTerminados((v) => v.filter((t) => t.id !== id)),
  };
}

/** Con la pestaña en segundo plano: el título lo dice hasta que se vuelve, y una notificación si ya hay permiso. */
function avisarFuera(acaban: Trabajo[]) {
  if (typeof document === "undefined" || document.visibilityState === "visible") return;
  const original = document.title.replace(/^\(✓\) /, "");
  document.title = `(✓) ${original}`;
  const volver = () => {
    if (document.visibilityState !== "visible") return;
    document.title = original;
    document.removeEventListener("visibilitychange", volver);
  };
  document.addEventListener("visibilitychange", volver);
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      for (const t of acaban) new Notification("HumanInk Studio", { body: t.estado === "hecho" ? `Terminado: ${t.titulo}` : `No se ha podido terminar: ${t.titulo}` });
    }
  } catch { /* sin notificaciones: queda el título */ }
}
